"use client";

import { useFormContext } from "react-hook-form";
import { useI18n } from "@/lib/i18n/hook";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Plus, Minus } from "lucide-react";

export function StepBasicInfo() {
  const { control, register, watch, setValue } = useFormContext();
  const { t } = useI18n();

  const hasSpouse = watch("basicInfo.familyStructure.hasSpouse");
  const children = watch("basicInfo.familyStructure.children") || [];

  const addChild = () => {
    const currentChildren = children || [];
    setValue("basicInfo.familyStructure.children", [
      ...currentChildren,
      { age: 0 },
    ]);
  };

  const removeChild = (index: number) => {
    const currentChildren = [...children];
    currentChildren.splice(index, 1);
    setValue("basicInfo.familyStructure.children", currentChildren);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <FormField
          control={control}
          name="basicInfo.name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("lifePlan.basicInfo.name")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("lifePlan.basicInfo.namePlaceholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="basicInfo.age"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("lifePlan.basicInfo.age")}</FormLabel>
              <FormControl>
                <div className="flex items-center space-x-2">
                  <NumberInput 
                    value={field.value}
                    onValueChange={(val) => field.onChange(val ?? 0)}
                    ref={field.ref}
                    name={field.name}
                    onBlur={field.onBlur}
                  />
                  <span>{t("common.age")}</span>
                </div>
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="basicInfo.gender"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>{t("lifePlan.basicInfo.gender.title")}</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="male" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {t("lifePlan.basicInfo.gender.male")}
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="female" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {t("lifePlan.basicInfo.gender.female")}
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="other" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {t("lifePlan.basicInfo.gender.other")}
                    </FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-4">
          <h3 className="text-lg font-medium">
            {t("lifePlan.basicInfo.familyStructure.title")}
          </h3>

          <FormField
            control={control}
            name="basicInfo.familyStructure.hasSpouse"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {t("lifePlan.basicInfo.familyStructure.hasSpouse")}
                  </FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {hasSpouse && (
            <FormField
              control={control}
              name="basicInfo.familyStructure.spouseAge"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("lifePlan.basicInfo.familyStructure.spouseAge")}
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <NumberInput 
                        value={field.value}
                        onValueChange={(val) => field.onChange(val ?? 0)}
                        ref={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                      />
                      <span>{t("common.age")}</span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">
                {t("lifePlan.basicInfo.familyStructure.children")}
              </h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addChild}
                className="h-8"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("lifePlan.basicInfo.familyStructure.addChild")}
              </Button>
            </div>

            {children.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("lifePlan.basicInfo.familyStructure.noChildren")}
              </p>
            ) : (
              <div className="space-y-4">
                {children.map((child: any, index: number) => (
                  <div key={index} className="flex items-center space-x-4">
                    <FormField
                      control={control}
                      name={`basicInfo.familyStructure.children.${index}.age`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <NumberInput
                                placeholder={t(
                                  "lifePlan.basicInfo.familyStructure.childrenHelp"
                                )}
                                value={field.value}
                                onValueChange={(val) => field.onChange(val ?? 0)}
                                ref={field.ref}
                                name={field.name}
                                onBlur={field.onBlur}
                              />
                              <span>{t("common.age")}</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeChild(index)}
                      className="h-8 w-8"
                    >
                      <Minus className="h-4 w-4" />
                      <span className="sr-only">
                        {t("lifePlan.basicInfo.familyStructure.removeChild")}
                      </span>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
